import { CalculationSection } from '@features/glossary/domain/calculations';
import { GLOSSARY } from './glossary';
import { SHARED_CALCULATIONS } from '@features/glossary/domain/shared-calculations';

export const CALCULATIONS = {
  visitAttribution: {
    label: 'Visit Attribution',
    items: {
      lciIncrementalLift: {
        label: 'Incremental Lift',
        definition: GLOSSARY.incrementalLift.definition,
        calculationType: 'simple',
        calculation:
          'Incremental Lift = (Exposed Visits - Control Visits) / Control Visits',
      },
      optimizationIndex: {
        label: 'Optimization Index',
        definition:
          'The index of performance to identify campaign elements that outperform the overall lift of the campaign.',
        calculationType: 'simple',
        calculation:
          'Optimization Index = LCI Incremental Lift of Dimension / LCI Incremental Lift of Campaign Overall',
      },
      costPerVisit: {
        label: 'Cost per Visit (CPV)',
        definition:
          'Measures total projected exposed visits per dollar spent on the ad campaign. Accounting for campaign duration. (Note: Ad Spend is an input received from the advertiser.)',
        calculationType: 'simple',
        calculation:
          'CPV = (Ad Spend * Days Elapsed / Campaign Duration) / Post Exposed Projected Visits',
      },
      costPerIncrementalvisit: {
        label: 'Cost per Incremental Visit (CPIV)',
        definition:
          'Measures the increase in total projected visits per dollar spent on the ad campaign. Accounting for campaign duration. (Note: Ad Spend is an input received from the advertiser.)',
        calculationType: 'simple',
        calculation:
          'CPIV = (Ad Spend * Days Elapsed / Campaign Duration) / Incremental Projected Visits',
      },
    },
  },
  projectedVisits: {
    label: 'Projected Visits',
    items: {
      platformMatchRate: {
        label: 'Platform Match Rate (PMR)',
        definition:
          'Percentage of all exposed users that are measurable (have visitation data)',
        calculationType: 'simple',
        calculation: 'PMR = Exposed Users with Location Data / Exposed Users',
      },
      totalProjectedVisits: {
        label: 'Total Projected Visits',
        definition: GLOSSARY.totalProjectedVisits.definition,
        calculationType: 'simple',
        calculation:
          'Total Projected Visits = (Post Exposed Visits / PMR) * Scaling Factor',
        additionalCalculations: [
          'PMR = Platform match rate, match of exposed users with location data',
          'Scaling Factor = Coverage adjustment accounting for the volume of visits we see compared to all visits',
        ],
        note: '* Projected visits may vary week over week. LCI results are cumulative and in some rare cases, may decrease.',
      },
      incrementalProjectedVisits: {
        label: 'Incremental Visits',
        definition: GLOSSARY.incrementalVisits.definition,
        calculationType: 'simple',
        calculation: 'Incremental Visits = Exposed Visits - Control Visits',
        additionalCalculations: [
          'Projected Incremental Visits = (Incremental Visits / PMR) * Scaling Factor',
          'PMR = Platform match rate, match of exposed users with location data',
          'Scaling Factor = Coverage adjustment accounting for the volume of visits we see compared to all visits',
        ],
      },
    },
  },
  salesLift: {
    label: 'Sales Lift',
    items: {
      transactionSize: {
        label: 'Transaction Size',
        definition: GLOSSARY.transactionSize.definition,
        calculationType: 'simple',
        calculation:
          'Transaction Size Lift = (Exposed Transaction Size / Control Transaction Size) - 1',
      },
      incrementalSales: {
        label: 'Incremental Sales',
        definition: GLOSSARY.incrementalSales.definition,
        calculationType: 'multi',
        calculationByType: {
          inStore:
            'Incremental Sales (In-Store) = Incremental Visits * Purchase Ratio * Transaction Size',
          online:
            'Incremental Sales (Online) = Exposed Users * (Sales Rate Exposed - Sales Rate Control)',
        },
        additionalCalculations: [
          'Purchase Ratio = (% of matched exposed who purchase) / (% of matched exposed who visit)',
          'Sales Rate = Penetration * Transaction Rate * Transaction Size',
        ],
      },
      incrementalSalesLift: {
        label: 'Incremental Sales Lift',
        definition: GLOSSARY.incrementalSalesLift.definition,
        calculation:
          '(Sales Rate Exposed - Sales Rate Control) / Sales Rate Control',
        calculationType: 'simple',
      },
      returnOnAdSpend: SHARED_CALCULATIONS.returnOnAdSpend,
      incrementalReturnOnAdSpend:
        SHARED_CALCULATIONS.incrementalReturnOnAdSpend,
      transactionRate: {
        label: 'Transaction Rate',
        definition: GLOSSARY.transactionRate.definition,
        calculation:
          'Transaction Rate Lift = (Exposed Transaction Rate / Control Transaction Rate) - 1',
        calculationType: 'simple',
      },
      penetration: {
        label: 'Penetration',
        definition: GLOSSARY.penetration.definition,
        calculation:
          'Penetration Lift = (Exposed Penetration / Control Penetration) - 1',
        calculationType: 'simple',
      },
      salesRate: {
        label: 'Sales Rate',
        definition: GLOSSARY.salesRate.definition,
        calculation:
          'Sales Rate = Penetration * Transaction Rate * Transaction Size',
        calculationType: 'simple',
      },
      totalProjectedSales: {
        label: 'Total Projected Sales',
        definition: GLOSSARY.totalProjectedSales.definition,
        calculationType: 'multi',
        calculationByType: {
          inStore:
            'Total Sales (In-Store) = Total Projected Visits * Purchase Ratio * Transaction Size',
          online: 'Total Sales (Online) = Exposed Users * Sales Rate Exposed',
        },
      },
    },
  },
} as const satisfies Record<
  'visitAttribution' | 'projectedVisits' | 'salesLift',
  CalculationSection
>;
